import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getNodesRoot, getNodesChildren } from "../../../../../app/src/services/buscarService";
import { generateSlug } from "../../../../../app/src/Utils/utils";

// Mapa slug ↔ id de los nodos de filtro del modal "Filtros" (OTT-1966 Fase 2, contrato §2.2/§2.3).
// El slug se deriva con el mismo slugificador que ya usa el resto del repo para URLs de
// contenido (generateSlug: minúsculas, sin acentos, espacios -> guiones); la API trabaja con ids.

const slugifyNode = (name) => generateSlug(name || "");

/**
 * @param {number|string} [interfazId]
 * @param {{loadRoot?: Function, loadChildrenOf?: Function}} [loaders] funciones de carga de nodos
 *   (misma firma que getNodesRoot/getNodesChildren). Por defecto, las del buscador; la Agenda
 *   (OTT-2011) pasa las suyas con type=agenda. Deben ser referencias estables (de módulo).
 */
export const useFilterNodes = (interfazId, { loadRoot = getNodesRoot, loadChildrenOf = getNodesChildren } = {}) => {
  const [roots, setRoots] = useState([]);
  const [childrenByParent, setChildrenByParent] = useState({});
  const [loading, setLoading] = useState(true);
  const loadedParents = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    loadedParents.current = new Set();
    setChildrenByParent({});
    loadRoot(interfazId, { signal: controller.signal })
      .then((res) => {
        if (cancelled) return;
        setRoots(res?.data || []);
      })
      .catch((err) => {
        if (!cancelled && err.name !== "AbortError") console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [interfazId, loadRoot]);

  const loadChildren = useCallback(
    (parentId) => {
      if (loadedParents.current.has(parentId)) {
        return Promise.resolve(childrenByParent[parentId] || []);
      }
      loadedParents.current.add(parentId);
      return loadChildrenOf(parentId, interfazId)
        .then((res) => {
          const data = res?.data || [];
          setChildrenByParent((prev) => ({ ...prev, [parentId]: data }));
          return data;
        })
        .catch((err) => {
          console.error(err);
          loadedParents.current.delete(parentId);
          return [];
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [interfazId, loadChildrenOf],
  );

  // Precarga los hijos de los padres al llegar la raíz (contrato §2.3: "o precargar al abrir
  // el modal para los nodos con has_children: true"); se hace aquí, una vez, en vez de en cada
  // apertura del modal.
  useEffect(() => {
    roots.filter((n) => n.has_children).forEach((n) => loadChildren(n.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots]);

  const { slugToId, idToSlug } = useMemo(() => {
    const allNodes = [...roots, ...Object.values(childrenByParent).flat()];
    const s2i = {};
    const i2s = {};
    allNodes.forEach((n) => {
      const slug = slugifyNode(n.name);
      s2i[slug] = n.id;
      i2s[n.id] = slug;
    });
    return { slugToId: s2i, idToSlug: i2s };
  }, [roots, childrenByParent]);

  // Slugs desconocidos se ignoran (contrato §4: "Si un slug no existe en el mapa, se ignora ese filtro").
  const resolveIds = useCallback((slugs) => (slugs || []).map((s) => slugToId[s]).filter((id) => id != null), [slugToId]);
  const resolveSlugs = useCallback((ids) => (ids || []).map((id) => idToSlug[id]).filter(Boolean), [idToSlug]);

  return {
    roots,
    childrenByParent,
    loading,
    loadChildren,
    resolveIds,
    resolveSlugs,
    // data vacío => no mostrar el botón "Filtros" (contrato §2.2, pantalla 17-B).
    hasFilters: roots.length > 0,
  };
};
